/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-list/table.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { EmptyState } from "@steelyard/theme";
import { PiChartBarDuotone } from "react-icons/pi";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { SearchInput } from "../../components/table/search-input";
import { orderColumns } from "./columns";
import { Filters } from "./filters";
import { getOrderPipeline, listOrders, ORDER_STATES } from "../../lib/services/ordering";
import { hrefWith, readPaging } from "../../lib/paging";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";
import { Badge, Button } from "rizzui";

/**
 * Ported from apps/admin/app/(app)/orders/page.tsx. Server-paged/filtered — nothing is
 * fetched-then-filtered client-side. `shortOnStock`/`reservationPending` are non-refusal flags,
 * not errors. The auto-approve line is stated text, not a toggle (there's nothing to post it to).
 */
export async function ordersListLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(params);
  const state = params.get("state") ?? undefined;
  const orderNumber = params.get("orderNumber") ?? undefined;
  const customerId = params.get("customerId") ?? undefined;
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;
  // The pipeline map is the API's own per-state count, for the state dropdown. A failed read costs
  // the options their counts, not the list.
  const [result, pipeline] = await Promise.all([
    listOrders({ page, pageSize, state, orderNumber, customerId, from, to }),
    getOrderPipeline().catch(() => null),
  ]);
  // The ?customerId chip names its customer from the rows' own server-sent name — never the id.
  const customerName = customerId ? (result.items.find((order) => order.customerId === customerId)?.customerName ?? null) : null;
  return { result, pipeline, state, orderNumber, customerId, from, to, customerName };
}

/** A state's count off the pipeline map, as the API sent it; `null` when the map could not be read. */
function stateCount(pipeline: Record<string, number | string> | null | undefined, state: string): number | null {
  if (!pipeline) return null;
  const count = Number(pipeline[state] ?? 0);
  return Number.isFinite(count) ? count : null;
}

export default function OrdersList() {
  const { result, pipeline, state, orderNumber, customerId, from, to, customerName } = useLoaderData() as Awaited<ReturnType<typeof ordersListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = (over: Record<string, string | undefined>) => hrefWith("/orders", searchParams, over);

  const filtered = Boolean(state || orderNumber || customerId || from || to);

  // Every real state is always offered — not just ones with orders today, or the dropdown could
  // never filter for a state that simply hasn't happened yet. Counted by the API's pipeline map;
  // the date and search filters narrow the list, not them.
  const stateOptions: StatusOption[] = [
    { label: "All states", href: query({ state: undefined, page: undefined }), active: !state },
    ...ORDER_STATES.map((row) => ({
      label: row.label,
      href: query({ state: row.state, page: undefined }),
      active: row.state === state,
      count: stateCount(pipeline, row.state),
      status: row.state,
    })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Orders"
        count={result.totalCount}
        description="Every order, searchable by number, state and date. Approval is mandatory for every credit order while the auto-approve flag is off."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Orders" }]}
      >
        {/* Stated, not a toggle — there is nothing to post it to. A fact about the list, not an
            action on it, so it stays with the description. */}
        <Badge variant="flat" color="info" rounded="md">
          Auto-approve below a threshold: Off
        </Badge>
      </ListHeader>

      <PagedTable
        actions={
          <Link to="/reports/pipeline">
            <Button as="span" variant="outline">
              <PiChartBarDuotone aria-hidden className="me-1.5 size-4" />
              Pipeline report
            </Button>
          </Link>
        }
        result={result}
        caption="Orders, with their state, type and total"
        columns={orderColumns(
          can(permissionState, "orders.administer"),
          can(permissionState, "orders.administer"),
          can(permissionState, "orders.override"),
          can(permissionState, "orders.advance"),
        )}
        getRowId={(row) => row.id}
        status={{ label: "State", options: stateOptions }}
        search={
          <SearchInput
            value={orderNumber}
            placeholder="Search by order number…"
            onSearch={(next) => navigate(query({ orderNumber: next, page: undefined }))}
          />
        }
        filters={<Filters value={{ from, to, customerId }} customerName={customerName} query={query} />}
        clear={{
          active: filtered,
          href: query({ orderNumber: undefined, state: undefined, from: undefined, to: undefined, customerId: undefined, page: undefined }),
        }}
        empty={
          <EmptyState
            title="No orders matched"
            action={
              filtered ? (
                <Link to="/orders">
                  <Button as="span" size="sm" variant="outline">
                    Clear the filters
                  </Button>
                </Link>
              ) : null
            }
          >
            {filtered
              ? "Nothing matched those filters. Clear them to see the whole queue."
              : "No order has been placed yet. The first one arrives from the customer portal."}
          </EmptyState>
        }
      />
    </div>
  );
}
